import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import MovieCard from "@/components/feature/MovieCard";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import type { MovieType } from "@/types/movie";

const typeFilters: { label: string; value: MovieType | "全部" }[] = [
  { label: "全部", value: "全部" },
  { label: "电影", value: "电影" },
  { label: "电视剧", value: "电视剧" },
  { label: "综艺", value: "综艺" },
];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const [input, setInput] = useState(query);
  const [typeFilter, setTypeFilter] = useState<MovieType | "全部">("全部");

  useDocumentMeta(
    query ? `搜索“${query}” - 国产在线观看` : "搜索 - 国产在线观看",
    "在国产在线观看搜索国产电影、电视剧与综艺节目，支持关键词模糊匹配与类型快速筛选。"
  );

  const results = useMemo(() => {
    const key = query.trim().toLowerCase();
    let list = movies;
    if (key) {
      list = list.filter((m) =>
        [m.title, m.synopsis, m.description, m.director, m.actors.join(" "), m.genres.join(" ")]
          .join(" ")
          .toLowerCase()
          .includes(key)
      );
    } else {
      list = [];
    }
    if (typeFilter !== "全部") {
      list = list.filter((m) => m.type === typeFilter);
    }
    return [...list].sort((a, b) => b.heat - a.heat);
  }, [query, typeFilter]);

  const hotPicks = useMemo(() => [...movies].sort((a, b) => b.heat - a.heat).slice(0, 4), []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (trimmed) {
      setParams({ q: trimmed });
    } else {
      setParams({});
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true" />
        <span className="text-foreground-800">搜索结果</span>
      </nav>

      <header className="mt-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">搜索结果</h1>
        <form onSubmit={submit} className="mt-4 flex flex-col gap-3 sm:flex-row" role="search">
          <div className="relative flex-1">
            <i
              className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg text-foreground-400"
              aria-hidden="true"
            />
            <input
              type="search"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入影视名称、演员或导演关键词…"
              aria-label="搜索关键词"
              className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>
          <button
            type="submit"
            className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-search-line text-base" aria-hidden="true" />
            搜索
          </button>
        </form>
      </header>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        {typeFilters.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => setTypeFilter(t.value)}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              typeFilter === t.value
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-secondary-100"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {query ? (
        <p className="mt-5 text-sm text-foreground-600">
          关键词 <span className="font-bold text-primary-600">“{query}”</span> 共找到{" "}
          <span className="font-bold text-primary-600">{results.length}</span> 部作品
        </p>
      ) : (
        <p className="mt-5 text-sm text-foreground-600">输入关键词，发现更多国产影视作品</p>
      )}

      {results.length > 0 ? (
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {results.map((movie) => (
            <MovieCard key={movie.id} movie={movie} highlight={query.trim() || undefined} />
          ))}
        </div>
      ) : (
        <div className="mt-6">
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-14 text-center">
            <i className="ri-search-eye-line text-4xl text-foreground-400" aria-hidden="true" />
            <p className="mt-3 text-base font-medium text-foreground-800">
              {query ? "未找到相关影视作品" : "快来搜索你喜欢的国产影视作品"}
            </p>
            <p className="mt-1 text-sm text-foreground-500">
              {query ? "换个关键词试试，或看看下面的热门推荐" : "试试搜索“狂飙”“流浪地球”“声生不息”"}
            </p>
          </div>

          <section className="mt-8" aria-labelledby="hot-picks-title">
            <h2
              id="hot-picks-title"
              className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950"
            >
              <i className="ri-fire-fill text-xl text-accent-500" aria-hidden="true" />
              热门推荐
            </h2>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {hotPicks.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}